import { useCallback } from 'react';
import type { IEmailValidationResult } from '@/modules/api/emailValidation';
import { fetcherInternal } from '@/modules/api/fetcher';
import {
  sendRegistrationEmailValidatedEvent,
  sendRegistrationEmailValidationFailureEvent,
} from '@/modules/googleAnalytics/events/registrationEvents';
import {
  extractEmailDomain,
  IS_EMAIL_VALIDATION_ENABLED,
  isEmailDomainWhitelisted,
} from '../helpers/emailValidation';

/**
 * Hook to handle email validation flow for registration
 *
 * Flow:
 * 1. Check if validation is enabled - if not, allow registration
 * 2. Check if email domain is whitelisted - if yes, skip validation
 * 4. If not whitelisted and not cached, call AbstractAPI to validate
 * 5. Cache the validation result for future use
 * 6. Return validation status (true = valid, false = invalid/disposable)
 */
export const useEmailValidation = () => {
  const validateEmail = useCallback(async (email: string): Promise<boolean> => {
    if (!IS_EMAIL_VALIDATION_ENABLED) return true;

    try {
      // Whitelist check
      if (isEmailDomainWhitelisted(email)) return true;

      // Extract domain
      const domain = extractEmailDomain(email);
      if (!domain) return false;

      // API validation
      const result = await fetcherInternal<IEmailValidationResult>(
        `/check-email?email=${encodeURIComponent(email)}`
      );

      sendRegistrationEmailValidatedEvent({
        quality_score: result.score,
        email: result.email,
        is_disposable_email: result.isDisposable,
      });

      const isValid = !result.isDisposable;

      return isValid;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      sendRegistrationEmailValidationFailureEvent({
        message: errorMessage,
      });
      console.error('Email validation service failed:', error);
      // On error, allow registration to continue
      return true;
    }
  }, []);

  return { validateEmail };
};
